import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Reusable client dropdown — populated from /api/admin/clients.
 *
 * Two visual variants:
 *   • default — labelled block, full width, padded input style
 *   • compact — inline, no label, matching the filter-row pill style on
 *     pages like /tasks. Set ``compact`` when the picker lives inside
 *     a filter bar.
 *
 * Excludes the SiteCentric superdomain by default (client work doesn't
 * belong to the platform tenant). Disabled clients are shown but
 * prefixed with "(disabled) " unless ``excludeDisabled`` is set.
 */
export default function ClientPicker({
    value,
    onChange,
    label = "Client",
    placeholder = "(select a client…)",
    anyOptionLabel,           // when set, shows a "(any)" first option
    includeSuperdomain = false,
    excludeDisabled = false,
    required = false,
    disabled = false,
    compact = false,
    width,                    // optional fixed width (e.g. "w-44")
    className = "",
    testId = "client-picker",
}) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/admin/clients`);
                setItems(data.items || []);
            } catch {
                /* ignore — dropdown stays empty */
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const visible = items.filter((c) => {
        if (!includeSuperdomain && c.is_superdomain) return false;
        if (excludeDisabled && c.status !== "active") return false;
        return true;
    });

    const selectClass = compact
        ? `bg-white/5 border border-white/10 rounded-md px-2 py-1 text-xs ${width || "w-56"} ${className}`
        : `w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm ${className}`;

    const select = (
        <select
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            disabled={disabled || loading}
            className={selectClass}
        >
            <option value="">
                {loading
                    ? "Loading clients…"
                    : (anyOptionLabel != null ? anyOptionLabel : placeholder)}
            </option>
            {visible.map((c) => {
                // Build the option label as a single, plain string so React
                // renders the <option> with exactly one text-node child.
                // Avoids any ambiguity that could surface the
                // "<span> cannot be a child of <option>" hydration warning
                // observed historically against this dropdown.
                const prefix = c.status !== "active" ? "(disabled) " : "";
                const sc = c.sitecentric_company_id ? ` · SC: ${c.sitecentric_company_id}` : "";
                const label = `${prefix}${c.company_name || c.client_name} — ${c.client_id}${sc}`;
                return (
                    <option key={c.client_id} value={c.client_id}>{label}</option>
                );
            })}
        </select>
    );

    if (compact) return select;

    return (
        <label className="block">
            <div className="text-xs text-white/50 mb-1">
                {label}{required && <span className="text-rose-400 ml-0.5">*</span>}
            </div>
            {select}
        </label>
    );
}
